package com.nonopn.skywatcher.common.api

import kotlinx.serialization.Serializable

/**
 * Minimum password length accepted by the backend on /register and /reset (PasswordTooShort below it).
 */
const val MinPasswordLength = 4

/**
 * Translatable failure of /login, /register or /reset. Error bodies are always { "code": "…", "message": "…" }:
 * only `code` is used, the English `message` is informative and never shown.
 */
enum class AuthError {
    InvalidCredentials,
    EmailAlreadyUsed,
    InvalidEmail,
    PasswordTooShort,
    InvalidResetCode,
    InvalidBody,
    ServerError,
    Generic
}

@Serializable
private data class AuthErrorBody(
    val code: String? = null
)

/** The `code` of an error body, or null when the body is absent or not the expected JSON. */
@Suppress("SwallowedException")
fun authErrorCodeOf(body: String?): String? {
    if (body.isNullOrBlank()) return null

    return try {
        JsonProvider.json.decodeFromString(AuthErrorBody.serializer(), body).code?.takeIf { it.isNotBlank() }
    } catch (_: IllegalArgumentException) {
        // SerializationException is an IllegalArgumentException: body that is not the documented JSON
        null
    }
}

/** Unknown code, absent body: generic message (never "wrong password" without the server saying so). */
fun authErrorForCode(code: String?): AuthError = when (code) {
    "InvalidCredentials" -> AuthError.InvalidCredentials
    "EmailAlreadyUsed" -> AuthError.EmailAlreadyUsed
    "InvalidEmail" -> AuthError.InvalidEmail
    "PasswordTooShort" -> AuthError.PasswordTooShort
    "InvalidResetCode" -> AuthError.InvalidResetCode
    "InvalidBody" -> AuthError.InvalidBody
    "InternalError" -> AuthError.ServerError
    else -> AuthError.Generic
}

fun authErrorOf(body: String?): AuthError = authErrorForCode(authErrorCodeOf(body))

/** Network failures and anything that is not a backend answer map to the generic message. */
fun Throwable.toAuthError(): AuthError = when (this) {
    is AuthErrorException -> this.error
    is BackendErrorException -> authErrorOf(body)
    else -> AuthError.Generic
}

/**
 * Error given back to the login / register screens: `message` is the already translated text to display.
 * Never put credentials in it.
 */
class AuthErrorException(
    val error: AuthError,
    message: String,
    cause: Throwable? = null
) : Exception(message, cause)
